import React, { useState, useEffect } from 'react';
import { Database, TrendingUp, Unlink, Plus, AlertCircle, Loader } from 'lucide-react';
import axios from 'axios';

const LinkedIHSCarlines = ({ opportunityId, onOpenBrowser }) => {
  const [linkedCarlines, setLinkedCarlines] = useState([]);
  const [totalVolume, setTotalVolume] = useState(0);
  const [loading, setLoading] = useState(true);
  const [unlinking, setUnlinking] = useState({});

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    if (opportunityId) {
      fetchLinkedCarlines();
    }
  }, [opportunityId]);

  const fetchLinkedCarlines = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${BACKEND_URL}/api/ihs-integration/opportunity/${opportunityId}/linked-carlines`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setLinkedCarlines(response.data.linked_carlines || []);
      // Use average annual volume for better representation of opportunity size
      setTotalVolume(response.data.total_avg_annual_volume || 0);
    } catch (error) {
      console.error('Error fetching linked carlines:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUnlink = async (linkId, carlineName) => {
    if (!window.confirm(`Unlink ${carlineName} from this opportunity?`)) {
      return;
    }

    setUnlinking({ ...unlinking, [linkId]: true });
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${BACKEND_URL}/api/ihs-integration/unlink-carline/${linkId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchLinkedCarlines(); // Refresh
    } catch (error) {
      alert(`Failed to unlink: ${error.response?.data?.detail || error.message}`);
    } finally {
      setUnlinking({ ...unlinking, [linkId]: false });
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-center h-32">
          <Loader className="w-8 h-8 text-blue-600 animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Database className="w-6 h-6 text-blue-600 mr-2" />
          <h3 className="text-xl font-semibold">Market Forecasts</h3>
        </div>
        <button
          onClick={onOpenBrowser}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Link Market Forecast
        </button>
      </div>

      {linkedCarlines.length === 0 ? (
        <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
          <AlertCircle className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-600 mb-2">No market forecasts linked yet</p>
          <p className="text-sm text-gray-500 mb-4">
            Link IHS/S&P Mobility carlines to track market volumes and forecasts
          </p>
          <button
            onClick={onOpenBrowser}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Browse Market Forecasts
          </button>
        </div>
      ) : (
        <>
          {/* Total Volume Card */}
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-blue-900">Total Market Volume (Avg Annual)</p>
                <p className="text-3xl font-bold text-blue-600 mt-1">
                  {totalVolume.toLocaleString()} <span className="text-lg">units/yr</span>
                </p>
                <p className="text-xs text-blue-700 mt-1">Lifetime average across all linked programs</p>
              </div>
              <TrendingUp className="w-10 h-10 text-blue-600" />
            </div>
            <p className="text-xs text-blue-700 mt-2">
              Sum of {linkedCarlines.length} linked IHS/S&P Mobility carline{linkedCarlines.length !== 1 ? 's' : ''}
            </p>
          </div>

          {/* Linked Carlines List */}
          <div className="space-y-3">
            <p className="text-sm font-medium text-gray-700">Linked Carlines:</p>
            {linkedCarlines.map((carline) => (
              <div
                key={carline.link_id}
                className="border border-gray-200 rounded-lg p-3 hover:border-blue-300 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h4 className="font-medium text-gray-900">{carline.production_nameplate}</h4>
                      <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                        carline.lifecycle_status === 'active' ? 'bg-green-100 text-green-700' :
                        carline.lifecycle_status === 'planning' ? 'bg-blue-100 text-blue-700' :
                        carline.lifecycle_status === 'ending' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-700'
                      }`}>
                        {carline.lifecycle_status}
                      </span>
                      {carline.auto_created && (
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                          Auto
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
                      <div>
                        <span className="font-medium">OEM:</span> {carline.sales_parent}
                      </div>
                      <div>
                        <span className="font-medium">Region:</span> {carline.region}
                      </div>
                      <div>
                        <span className="font-medium">Program:</span> {carline.program || 'N/A'}
                      </div>
                      <div>
                        <div className="flex items-center text-blue-600">
                          <TrendingUp className="w-3 h-3 mr-1" />
                          <span className="font-medium">{(carline.avg_annual_volume || 0).toLocaleString()} units/yr</span>
                        </div>
                        {carline.total_lifetime_volume > 0 && (
                          <p className="text-xs text-gray-500 mt-0.5">
                            Total: {(carline.total_lifetime_volume || 0).toLocaleString()} units
                          </p>
                        )}
                      </div>
                    </div>
                    {carline.source === 'program_part' && (
                      <div className="mt-1 text-xs text-blue-600">
                        Linked through Program 360{carline.linked_part_number ? ` part ${carline.linked_part_number}` : ''}
                      </div>
                    )}
                    {carline.sop && (
                      <div className="text-xs text-gray-500 mt-1">
                        SOP: {new Date(carline.sop).toLocaleDateString()} 
                        {carline.eop && ` - EOP: ${new Date(carline.eop).toLocaleDateString()}`}
                      </div>
                    )}
                  </div>
                  {carline.can_unlink !== false && (
                    <button
                      onClick={() => handleUnlink(carline.link_id, carline.production_nameplate)}
                      disabled={unlinking[carline.link_id]}
                      className="ml-3 p-2 text-red-600 hover:bg-red-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Unlink carline"
                    >
                      {unlinking[carline.link_id] ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-600"></div>
                      ) : (
                        <Unlink className="w-4 h-4" />
                      )}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default LinkedIHSCarlines;
